import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { matchesAPI } from '@/lib/api';
import { formatSeriesDate } from '@/lib/matchTime';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Repeat, Plus } from 'lucide-react';

// Monday-first, like the backend (Python weekday: Monday = 0).
const WEEKDAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
const WEEKDAY_SHORT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

const remainingLabel = (remaining) =>
  remaining == null ? ', finché non la fermi' : remaining > 1 ? ` (ancora ${remaining})` : ' (ultima)';

// The league's repeats: what comes out next, and the rule from the next match on.
export const LeagueSeriesManager = ({ leagueId }) => {
  const [series, setSeries] = useState(null);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    matchesAPI.getLeagueSeries(leagueId)
      .then((res) => setSeries(res.data.series))
      .catch(() => setSeries([]));
  }, [leagueId]);

  const save = async (seriesId, data) => {
    try {
      const res = await matchesAPI.updateSeries(seriesId, data);
      setSeries((list) => list.map((s) => (s.series_id === seriesId ? res.data : s)));
      setDraft(null);
      if (data.active && !res.data.active) toast.error('Non resta nessuna partita: indica quante volte ripetere');
      else toast.success(data.active === false ? 'Ripetizione fermata' : 'Ripetizione aggiornata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  if (!series) return <Skeleton className="h-32" />;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Repeat className="h-5 w-5" />
          Ripetizioni
        </CardTitle>
        <Link to={`/matches/new?league=${leagueId}&repeat=1`}>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Nuova
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-3">
        {series.length === 0 && (
          <p className="text-center text-muted-foreground py-6">
            Nessuna ripetizione: nasce insieme alla prima partita.
          </p>
        )}
        {series.map((s) => (draft?.series_id === s.series_id ? (
          <div key={s.series_id} className="rounded-lg border-2 border-purple-300 dark:border-purple-900 p-3 space-y-3">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Giorni in cui giocate</Label>
              <div className="flex gap-1.5 mt-1">
                {WEEKDAY_SHORT.map((label, day) => {
                  const on = draft.weekdays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-label={WEEKDAYS[day]}
                      aria-pressed={on}
                      onClick={() => setDraft({
                        ...draft,
                        weekdays: on ? draft.weekdays.filter((d) => d !== day) : [...draft.weekdays, day],
                      })}
                      className={`h-9 w-9 rounded-full text-sm font-semibold border transition-colors ${
                        on ? 'bg-purple-600 text-white border-purple-600' : 'bg-background text-muted-foreground border-border'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Ora partita</Label>
                <Input type="time" value={draft.start_time} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">A che ora esce</Label>
                <Input type="time" value={draft.publish_time} onChange={(e) => setDraft({ ...draft, publish_time: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Quante ancora</Label>
                <Input
                  type="number"
                  min={1}
                  max={199}
                  placeholder="Sempre"
                  value={draft.count ?? ''}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setDraft({ ...draft, count: n > 0 ? Math.min(199, n) : null });
                  }}
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm">Admin titolari</Label>
              <Switch
                checked={draft.admins_confirmed}
                onCheckedChange={(v) => setDraft({ ...draft, admins_confirmed: v })}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Vale dalla prossima partita che esce: quelle già uscite restano come sono.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!draft.weekdays.length || !draft.title.trim() || !draft.start_time || !draft.publish_time}
                onClick={() => save(s.series_id, {
                  title: draft.title,
                  weekdays: draft.weekdays,
                  start_time: draft.start_time,
                  publish_time: draft.publish_time,
                  count: draft.count,
                  admins_confirmed: draft.admins_confirmed,
                })}
              >
                Salva
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Annulla</Button>
            </div>
          </div>
        ) : (
          <div key={s.series_id} className="rounded-lg border p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium truncate">{s.title}</p>
                <p className="text-sm text-muted-foreground">
                  {s.weekdays.map((d) => WEEKDAYS[d]).join(', ')} alle {s.start_time} · esce alle {s.publish_time}
                  {s.admins_confirmed ? ' · admin titolari' : ''}
                </p>
              </div>
              <Badge variant={s.active ? 'default' : 'secondary'} className="shrink-0">
                {s.active ? 'Attiva' : 'Ferma'}
              </Badge>
            </div>
            {s.next && (
              <p className="text-xs text-muted-foreground">
                Prossima: {formatSeriesDate(s.next.next_date_time)}, esce {formatSeriesDate(s.next.publish_at)}
                {remainingLabel(s.next.remaining)}.
              </p>
            )}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...s, count: s.remaining || null })}>
                Modifica
              </Button>
              <Button size="sm" variant="outline" onClick={() => save(s.series_id, { active: !s.active })}>
                {s.active ? 'Ferma' : 'Riattiva'}
              </Button>
            </div>
          </div>
        )))}
      </CardContent>
    </Card>
  );
};
